import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Package, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const FLOOR_NAMES = [
  "Ground", "First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh",
  "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth", "Thirteenth", "Fourteenth",
  "Fifteenth", "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth", "Twentieth",
];
const CONDITIONS = ["New", "Good", "Fair", "Needs Repair", "Damaged", "Disposed"];
const ASSET_TYPES = ["Fixed", "Movable"];

const emptyForm = {
  property_id: "",
  floor: "",
  unit_id: "",
  room_label: "",
  category: "",
  subcategory: "",
  brand: "",
  asset_type: "Movable",
  quantity: 1,
  condition: "Good",
  photos: [],
  remarks: "",
};

function Field({ label, full = false, children }) {
  return (
    <label className={full ? "block md:col-span-2" : "block"}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

// Single-slot JPG photo uploader (max 200KB). Used 3x for the 3-photo set.
function PhotoSlot({ slot, value, onChange, disabled }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (file) => {
    if (!file) return;
    if (!/^image\/jpe?g$/.test(file.type)) {
      toast.error("Only JPG files are allowed");
      return;
    }
    if (file.size > 200 * 1024) {
      toast.error("Each photo must be 200 KB or smaller");
      return;
    }
    const fd = new FormData();
    fd.append("kind", "inst_movable_photo");
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ url: data.url, public_id: data.public_id });
      toast.success(`Photo ${slot + 1} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid={`mv-photo-slot-${slot}`}
      className="relative border border-dashed border-[#e5e0d8] rounded-sm bg-[#f9f8f6] aspect-square overflow-hidden flex items-center justify-center"
    >
      {value?.url ? (
        <>
          <img src={value.url} alt={`slot ${slot}`} className="w-full h-full object-cover" />
          {!disabled ? (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="absolute top-1 right-1 bg-white/90 hover:bg-white border border-[#e5e0d8] text-[#9e2a2b] rounded-sm p-1"
              data-testid={`mv-photo-remove-${slot}`}
            >
              <Trash2 size={12} strokeWidth={1.5} />
            </button>
          ) : null}
        </>
      ) : (
        <label className="flex flex-col items-center justify-center cursor-pointer text-[#6b655b] hover:text-[#234333] text-[11px] gap-1 p-2 text-center">
          {busy ? (
            <span data-testid={`mv-photo-busy-${slot}`}>Uploading…</span>
          ) : (
            <>
              <ImageIcon size={18} strokeWidth={1.5} />
              <span>JPG · 200 KB</span>
              <span className="text-[9px] uppercase tracking-wider">Photo {slot + 1}</span>
            </>
          )}
          <input
            type="file"
            accept="image/jpeg,image/jpg"
            disabled={disabled || busy}
            className="hidden"
            data-testid={`mv-photo-input-${slot}`}
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </label>
      )}
    </div>
  );
}

export default function MovableItems() {
  const { role, user } = useAuth();
  const writable = role === "institute_admin";
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]); // for the currently selected category
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterProp, setFilterProp] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/institute/movable-items"),
        api.get("/institute/property-options"),
        api.get("/masters/movable_item_category"),
      ]);
      setItems(a.data);
      setProperties(b.data);
      setCategories((c.data || []).map((x) => x.name));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAll();
  }, []);

  // Fetch subcategories when category changes (in the form)
  useEffect(() => {
    if (!form.category) {
      setSubcategories([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(
          `/masters/movable_item_subcategory?parent=${encodeURIComponent(form.category)}`,
        );
        setSubcategories((data || []).map((x) => x.name));
      } catch {
        setSubcategories([]);
      }
    })();
  }, [form.category]);

  const propById = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.id, p])),
    [properties],
  );

  // Floor options based on selected property's floors_count
  const floorOptions = useMemo(() => {
    const p = propById[form.property_id];
    const n = Math.max(1, Number(p?.floors_count) || 1);
    return FLOOR_NAMES.slice(0, Math.min(n, FLOOR_NAMES.length));
  }, [propById, form.property_id]);

  // Room options: units of the selected property filtered by selected floor
  const roomOptions = useMemo(() => {
    const p = propById[form.property_id];
    if (!p) return [];
    return (p.units || [])
      .filter((u) => !form.floor || (u.floor || "") === form.floor)
      .sort((a, b) =>
        (a.assigned_room_no || a.name).localeCompare(b.assigned_room_no || b.name),
      );
  }, [propById, form.property_id, form.floor]);

  const openCreate = () => {
    setEditing(null);
    const propId = filterProp || properties[0]?.id || "";
    const p = propById[propId];
    const floors = p ? FLOOR_NAMES.slice(0, Math.max(1, p.floors_count || 1)) : ["Ground"];
    setForm({
      ...emptyForm,
      property_id: propId,
      floor: floors[0] || "Ground",
      category: categories[0] || "",
    });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...emptyForm, ...it });
    setOpen(true);
  };

  // Reset floor & room when property changes
  useEffect(() => {
    if (!open) return;
    if (!floorOptions.includes(form.floor)) {
      set("floor", floorOptions[0] || "");
    }
  }, [floorOptions.join("|"), open]);

  // Reset room when floor changes
  useEffect(() => {
    if (!open) return;
    if (form.unit_id && !roomOptions.some((r) => r.id === form.unit_id)) {
      set("unit_id", "");
      set("room_label", "");
    }
  }, [form.floor, roomOptions.length]);

  const setPhoto = (idx, next) => {
    setForm((f) => {
      const photos = [...(f.photos || [])];
      if (next === null) {
        photos.splice(idx, 1);
      } else {
        photos[idx] = next;
      }
      return { ...f, photos: photos.filter(Boolean).slice(0, 3) };
    });
  };

  const save = async () => {
    if (!form.property_id) return toast.error("Select a building");
    if (!form.floor) return toast.error("Select a floor");
    if (!form.category) return toast.error("Select a category");
    if (Number(form.quantity) < 1) return toast.error("Quantity must be at least 1");
    setSaving(true);
    const payload = {
      ...form,
      quantity: Number(form.quantity) || 1,
      room_label:
        form.unit_id && roomOptions.find((r) => r.id === form.unit_id)
          ? (() => {
              const r = roomOptions.find((x) => x.id === form.unit_id);
              return r.assigned_room_no || r.name;
            })()
          : "",
    };
    try {
      if (editing) {
        const { data } = await api.put(`/institute/movable-items/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/institute/movable-items", payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/movable-items/${confirmDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = filterProp ? items.filter((i) => i.property_id === filterProp) : items;

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="movable-items-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Treasury</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Movable Items</h1>
          <p className="mt-2 text-sm text-[#6b655b]">
            Inventory of furniture, electronics, lab equipment and other movables across your campus.
            {user?.institute_name ? ` (${user.institute_name})` : ""}
          </p>
        </div>
        {writable ? (
          <button
            onClick={openCreate}
            disabled={properties.length === 0}
            data-testid="mv-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] disabled:opacity-40 text-white text-sm px-5 py-2.5 rounded-sm whitespace-nowrap"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Item
          </button>
        ) : null}
      </div>

      {properties.length > 1 ? (
        <div className="mb-5 flex flex-wrap gap-2">
          <button
            onClick={() => setFilterProp("")}
            data-testid="mv-filter-all"
            className={`px-3 py-1.5 text-xs tracking-wide uppercase border rounded-sm ${
              !filterProp ? "bg-[#234333] text-white border-[#234333]" : "bg-white text-[#6b655b] border-[#e5e0d8]"
            }`}
          >
            All Buildings
          </button>
          {properties.map((p) => (
            <button
              key={p.id}
              onClick={() => setFilterProp(p.id)}
              data-testid={`mv-filter-${p.id}`}
              className={`px-3 py-1.5 text-xs tracking-wide uppercase border rounded-sm ${
                filterProp === p.id
                  ? "bg-[#234333] text-white border-[#234333]"
                  : "bg-white text-[#6b655b] border-[#e5e0d8] hover:bg-[#efece6]"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="mv-empty"
          className="border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center bg-white"
        >
          <Package size={28} strokeWidth={1.5} className="mx-auto text-[#6b655b] mb-3" />
          <p className="text-sm text-[#6b655b]">No movable items recorded yet.</p>
          {properties.length === 0 ? (
            <p className="text-[11px] text-[#9e2a2b] mt-1">
              Buildings are managed by the trust admin. Once buildings are added, you can start
              recording movable items here.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="mv-table">
            <thead className="bg-[#f9f8f6]">
              <tr>
                {["Photo", "Item", "Category", "Building / Floor / Room", "Brand", "Type", "Qty", "Condition", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[10px] tracking-wide uppercase text-[#6b655b] font-semibold text-left"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#efece6]">
              {filtered.map((it) => {
                const p = propById[it.property_id];
                return (
                  <tr
                    key={it.id}
                    data-testid={`mv-row-${it.id}`}
                    className="hover:bg-[#f9f8f6]/60"
                  >
                    <td className="px-4 py-3">
                      {it.photos?.[0]?.url ? (
                        <img
                          src={it.photos[0].url}
                          alt={it.subcategory || it.category}
                          className="w-12 h-12 object-cover rounded-sm border border-[#e5e0d8]"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-[#efece6] rounded-sm" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#1c1a17]">
                      <div className="font-medium">{it.subcategory || it.category}</div>
                      {it.remarks ? (
                        <div className="text-[11px] text-[#6b655b]">{it.remarks}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.category}</td>
                    <td className="px-4 py-3 text-[#6b655b] text-[12px]">
                      <div>{p?.name || "—"}</div>
                      <div className="text-[11px]">
                        {it.floor || "—"}
                        {it.room_label ? ` · ${it.room_label}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.brand || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
                          it.asset_type === "Fixed"
                            ? "bg-[#234333]/10 text-[#234333] border-[#234333]/30"
                            : "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/30"
                        }`}
                      >
                        {it.asset_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-[#1c1a17]">{it.quantity}</td>
                    <td className="px-4 py-3 text-[#6b655b]">{it.condition}</td>
                    <td className="px-4 py-3 text-right">
                      {writable ? (
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => openEdit(it)}
                            data-testid={`mv-edit-${it.id}`}
                            className="p-2 text-[#234333] hover:bg-[#efece6] rounded-sm"
                          >
                            <Pencil size={14} strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(it)}
                            data-testid={`mv-delete-${it.id}`}
                            className="p-2 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 rounded-sm"
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mv-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Movable Item" : "Add Movable Item"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <Field label="Building">
              <select
                data-testid="mv-property-select"
                className={inputCls}
                value={form.property_id}
                onChange={(e) => set("property_id", e.target.value)}
              >
                <option value="">— Select —</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Floor">
              <select
                data-testid="mv-floor-select"
                className={inputCls}
                value={form.floor}
                disabled={!form.property_id}
                onChange={(e) => set("floor", e.target.value)}
              >
                {floorOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              {form.property_id ? (
                <div className="text-[11px] text-[#6b655b] mt-1">
                  Building has {floorOptions.length} floor{floorOptions.length === 1 ? "" : "s"}.
                </div>
              ) : null}
            </Field>
            <Field label="Room" full>
              <select
                data-testid="mv-room-select"
                className={inputCls}
                value={form.unit_id}
                disabled={!form.floor || roomOptions.length === 0}
                onChange={(e) => set("unit_id", e.target.value)}
              >
                <option value="">
                  {roomOptions.length === 0 ? "— No rooms recorded for this floor —" : "— Optional · Select a room —"}
                </option>
                {roomOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.assigned_room_no ? `${r.assigned_room_no} · ${r.name}` : r.name}
                    {r.unit_type ? ` (${r.unit_type})` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Category">
              <select
                data-testid="mv-category-select"
                className={inputCls}
                value={form.category}
                onChange={(e) => {
                  set("category", e.target.value);
                  set("subcategory", "");
                }}
              >
                <option value="">— Select —</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Subcategory">
              <select
                data-testid="mv-subcategory-select"
                className={inputCls}
                value={form.subcategory}
                disabled={!form.category || subcategories.length === 0}
                onChange={(e) => set("subcategory", e.target.value)}
              >
                <option value="">
                  {form.category && subcategories.length === 0
                    ? "— No subcategories — add in Masters —"
                    : "— Optional —"}
                </option>
                {subcategories.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Brand">
              <input
                type="text"
                data-testid="mv-brand-input"
                className={inputCls}
                value={form.brand}
                maxLength={64}
                onChange={(e) => set("brand", e.target.value)}
              />
            </Field>
            <Field label="Asset Type">
              <select
                data-testid="mv-asset-type-select"
                className={inputCls}
                value={form.asset_type}
                onChange={(e) => set("asset_type", e.target.value)}
              >
                {ASSET_TYPES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Quantity">
              <input
                type="number"
                min="1"
                data-testid="mv-quantity-input"
                className={inputCls}
                value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)}
              />
            </Field>
            <Field label="Condition">
              <select
                data-testid="mv-condition-select"
                className={inputCls}
                value={form.condition}
                onChange={(e) => set("condition", e.target.value)}
              >
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Photos · JPG only · max 3 · 200 KB each" full>
              <div className="grid grid-cols-3 gap-3" data-testid="mv-photos">
                {[0, 1, 2].map((idx) => (
                  <PhotoSlot
                    key={idx}
                    slot={idx}
                    value={form.photos?.[idx]}
                    onChange={(next) => setPhoto(idx, next)}
                    disabled={!writable}
                  />
                ))}
              </div>
            </Field>
            <Field label="Remarks" full>
              <textarea
                rows={3}
                data-testid="mv-remarks-input"
                className={inputCls}
                value={form.remarks}
                maxLength={500}
                onChange={(e) => set("remarks", e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              data-testid="mv-cancel"
              className="px-5 py-2 text-sm text-[#6b655b] hover:bg-[#efece6] rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || !writable}
              data-testid="mv-save"
              className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] disabled:opacity-40 text-white text-sm px-5 py-2 rounded-sm"
            >
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="mv-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete movable item?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.subcategory || confirmDelete?.category} · qty {confirmDelete?.quantity}.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mv-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              data-testid="mv-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7e2122] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
